import { useEffect, useState } from 'react'
import { ulid } from 'ulid'
import {
  AvailabilitySlot,
  AvailabilitySlotId,
  UnavailabilitySlot,
} from '../types/availability-interface'
import { groupAvailabilities } from '../utils'

export const useDraftAvailabilitySlots = (availabilitySlots?: AvailabilitySlot[]) => {
  const [draftAvailabilitySlots, setDraftAvailabilitySlots] = useState<AvailabilitySlot[]>([])
  const [initiated, setInitiated] = useState(false)

  const reinitiate = () => {
    setInitiated(false)
  }

  useEffect(() => {
    if (availabilitySlots && availabilitySlots.length > 0 && !initiated) {
      setDraftAvailabilitySlots(availabilitySlots)
      setInitiated(true)
    }
  }, [availabilitySlots, initiated])

  const updateSlot = (slot: AvailabilitySlot) => {
    setDraftAvailabilitySlots(prev =>
      prev.map(prevDay => (prevDay.id === slot.id ? slot : prevDay)),
    )
  }

  const removeSlots = (slotIds: AvailabilitySlotId[]) => {
    setDraftAvailabilitySlots(prev => prev?.filter(prevDay => !slotIds.includes(prevDay.id)))
  }

  const addSlot = (slot: Omit<AvailabilitySlot, 'id'>) => {
    const id = ulid()
    setDraftAvailabilitySlots(prev => prev && [...prev, { ...slot, id }])
  }

  const { weekdaysSlots, dateSpecificSlots } = groupAvailabilities(draftAvailabilitySlots)

  return {
    draftAvailabilitySlots,
    addSlot,
    removeSlots,
    updateSlot,
    weekdaysSlots,
    dateSpecificSlots,
    reinitiate,
  }
}

export const useDraftUnavailabilitySlots = (unavailabilitySlots?: UnavailabilitySlot[]) => {
  const [slots, setSlots] = useState<UnavailabilitySlot[]>([])
  const [initiated, setInitiated] = useState(false)

  const reinitiate = () => {
    setInitiated(false)
  }

  useEffect(() => {
    if (unavailabilitySlots && unavailabilitySlots.length > 0 && !initiated) {
      setSlots(unavailabilitySlots)
      setInitiated(true)
    }
  }, [unavailabilitySlots, initiated])

  const updateSlot = (slot: UnavailabilitySlot, slotIndex: number) => {
    setSlots(prev => prev.map((prevDay, i) => (i === slotIndex ? slot : prevDay)))
  }

  const removeSlot = (slotIndex: number) => {
    setSlots(prev => prev?.filter((_, index) => index !== slotIndex))
  }

  const addSlot = (slot: UnavailabilitySlot) => {
    setSlots(prev => prev && [...prev, slot])
  }

  return {
    slots,
    addSlot,
    removeSlot,
    updateSlot,
    reinitiate,
  }
}
